/**
 * Keyword matches at once, semantic ("related") matches as they arrive.
 *
 * Keyword search is local and synchronous, so its results render on the same
 * keystroke. Semantic search needs the query embedded by the Edge Function,
 * so it is debounced and reported by status: the dialog shows a loading row
 * below the keyword matches while it runs, then the related tasks the keyword
 * pass did not already find.
 */
import { useEffect, useMemo, useState } from "react";

import type { Language } from "@/lib/i18n/types";
import { PAGE_INFO_MAX_QUERY_CHARS } from "./embedConfig";
import { indexChunks, keywordSearch, type KeywordHit } from "./keywordSearch";
import type { PageInfoChunk } from "./pageInfoChunks";
import {
  buildSemanticIndex,
  embedQuery,
  loadIndexFile,
  semanticSearch,
  type SemanticHit,
} from "./semanticSearch";

/** Shorter queries are word fragments; meaning needs a little more to go on. */
export const SEMANTIC_MIN_CHARS = 3;
export const SEMANTIC_DEBOUNCE_MS = 250;

export type SemanticStatus = "idle" | "loading" | "ready" | "unavailable";

export interface PageInfoSearchResult {
  query: string;
  keyword: KeywordHit[];
  /** Semantic hits the keyword pass did not already return. */
  related: SemanticHit[];
  semanticStatus: SemanticStatus;
}

interface SemanticState {
  query: string;
  status: SemanticStatus;
  hits: SemanticHit[];
}

export function usePageInfoSearch(
  rawQuery: string,
  chunks: readonly PageInfoChunk[],
  language: Language
): PageInfoSearchResult {
  const query = rawQuery.trim();
  const keywordIndex = useMemo(() => indexChunks(chunks), [chunks]);
  const keyword = useMemo(() => keywordSearch(keywordIndex, query), [keywordIndex, query]);
  const wantsSemantic =
    query.length >= SEMANTIC_MIN_CHARS && query.length <= PAGE_INFO_MAX_QUERY_CHARS;

  const [semantic, setSemantic] = useState<SemanticState>({ query: "", status: "idle", hits: [] });

  useEffect(() => {
    if (!wantsSemantic) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      Promise.all([loadIndexFile(language), embedQuery(query)])
        .then(([file, vector]) => {
          if (cancelled) return;
          const index = buildSemanticIndex(file, chunks);
          setSemantic(
            index
              ? {
                  query,
                  status: "ready",
                  hits: semanticSearch(index, vector, chunks, file.noiseFloor),
                }
              : { query, status: "unavailable", hits: [] }
          );
        })
        .catch(() => {
          if (!cancelled) setSemantic({ query, status: "unavailable", hits: [] });
        });
    }, SEMANTIC_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, wantsSemantic, chunks, language]);

  const keywordKeys = new Set(keyword.map((hit) => hit.chunk.key));
  // A result for an earlier query is still loading as far as this one is concerned.
  const current = semantic.query === query ? semantic : null;
  const semanticStatus: SemanticStatus = !wantsSemantic ? "idle" : (current?.status ?? "loading");
  const related =
    current?.status === "ready"
      ? current.hits.filter((hit) => !keywordKeys.has(hit.chunk.key))
      : [];

  return { query, keyword, related, semanticStatus };
}
